import * as React from 'react';
import {useHistory} from 'react-router-dom';
import {DateTime as luxon} from 'luxon';
import {sortBy} from 'lodash-es';
import {
  Box,
  Button,
  Cluster,
  Cover,
  Divider,
  Expand,
  FormItem,
  Heading,
  Help,
  Icon,
  ISelectOption,
  ITableCol,
  Notification,
  Pagination,
  Paragraph,
  Select,
  Sidebar,
  Spinner,
  Stack,
  Table,
  TdLink,
  Template,
  TextInput,
  useValidateForm,
} from '@pluto-tv/assemble';

import {useAppPermissions} from 'app/permissions';

import runLimitRoutes from 'routes/runLimits.routes';

import NotAuthorized from 'components/notAuthorized';
import {TableActions} from 'components/tableActions';
import CrudError from 'components/crudError';
import {FormSearchWrapper} from 'components/formSearchWrapper';

import {useFindQuery} from 'features/licensedTitles/licensedTitlesApi';
import {useFindQuery as useFindPartnersQuery} from 'features/partners/partnersApi';

import {useUserRegions} from 'helpers/useUserRegions';
import {programmingTypes} from 'helpers/programmingTypes';

import {ILicensedTitle, ILicensedTitleSearch, IRunLimitRes} from 'models/licensedTitles';

import {searchLicensedTitlesValidator} from '../validators';
import {generateRunLimitUri, useRunLimitCache} from '../utils';

const TABLE_COLUMN_NAME = {
  Title: 1,
  Partner: 2,
  'Active Region(s)': 3,
} as const;

const SECONDS_IN_DAY = 60 * 60 * 24;

const formatDate = (seconds: number): string =>
  luxon.fromSeconds(seconds).setZone('America/Los_Angeles').toFormat('LL/dd/yyyy');

interface IRunLimitsTd {
  licensedTitleId: string;
  getPromise: (licensedTitleId: string) => Promise<IRunLimitRes>;
  isWindow?: boolean;
}

const RunLimits = ({licensedTitleId, getPromise, isWindow = false}: IRunLimitsTd): JSX.Element => {
  const [isLoading, setIsLoading] = React.useState(true);
  const [hasError, setHasError] = React.useState(false);
  const [runLimits, setRunLimits] = React.useState(0);
  const [windowText, setWindowText] = React.useState<string[]>(['N/A']);

  React.useEffect(() => {
    (async () => {
      setIsLoading(true);

      const url = generateRunLimitUri(licensedTitleId);

      try {
        const {data} = await getPromise(url);

        if (data && data.length > 0) {
          setRunLimits(data.length);

          if (isWindow) {
            const sorted = sortBy(data, rl => rl.startDate.seconds);

            let earliest = sorted[0].startDate.seconds;
            let latest = sorted[0].endDate.seconds;

            const windows: string[] = [];

            for (let i = 1; i < sorted.length; i++) {
              // More than a day gap
              if (latest + SECONDS_IN_DAY + 2 < sorted[i].startDate.seconds) {
                windows.push(`${formatDate(earliest)} - ${formatDate(latest)}`);

                earliest = sorted[i].startDate.seconds;
                latest = sorted[i].endDate.seconds;
              } else {
                if (earliest > sorted[i].startDate.seconds) {
                  earliest = sorted[i].startDate.seconds;
                }
                if (latest < sorted[i].endDate.seconds) {
                  latest = sorted[i].endDate.seconds;
                }
              }

              if (i === sorted.length - 1) {
                windows.push(`${formatDate(earliest)} - ${formatDate(latest)}`);
              }
            }

            if (windows.length === 0) {
              windows.push(`${formatDate(earliest)} - ${formatDate(latest)}`);
            }

            setWindowText(windows);
          }
        }
      } catch (e) {
        setHasError(true);
      }

      setIsLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getPromise]);

  if (isLoading) {
    return <Spinner size='small' />;
  }

  if (hasError) {
    return <Help state='error'>Error Loading</Help>;
  }

  return <>{isWindow ? <Stack>{windowText.map(text => text)}</Stack> : runLimits}</>;
};

export default (): JSX.Element => {
  const history = useHistory();
  const {ableTo} = useAppPermissions();

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [isSearchExpanded, setIsSearchExpanded] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [searchObj, setSearchObj] = React.useState<ILicensedTitleSearch>({});
  const [skipSearch, setSkipSearch] = React.useState(true);

  const {data: partners, isFetching: isFetchingPartners, isError: isErrorPartners} = useFindPartnersQuery();

  const {activeRegions, isFetching: isUserRegionsFetching, isError: isUserRegionsError} = useUserRegions();

  const {getPromise} = useRunLimitCache<IRunLimitRes>();

  const [sortCol, setSortCol] = React.useState<keyof typeof TABLE_COLUMN_NAME>('Title');
  const [sortDir, setSortDir] = React.useState<1 | 2>(1);

  const {
    data: licensedTitles,
    isFetching,
    isError,
  } = useFindQuery(
    {
      offset: page * rowsPerPage,
      limit: rowsPerPage,
      sortfield: TABLE_COLUMN_NAME[sortCol],
      sortdirection: sortDir,
      ...searchObj,
    },
    {refetchOnMountOrArgChange: true, skip: skipSearch},
  );

  const {
    model: searchModel,
    onChange: searchOnChange,
    setFields: searchSetFields,
    setModel: setSearchModel,
    form: searchForm,
    // state: searchState,
    onBlur: searchOnBlur,
  } = useValidateForm<ILicensedTitleSearch>(searchLicensedTitlesValidator, 'immediate');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => setSearchModel({}), []);

  React.useEffect(() => {
    if (activeRegions && activeRegions.length > 0) {
      if (!searchObj.regions || searchObj.regions.length === 0) {
        setSearchObj({
          ...searchObj,
          regions: activeRegions.map(region => region.code.toLowerCase()),
        });
      }

      setSkipSearch(false);
    } else {
      setSkipSearch(true);
    }
  }, [searchObj, activeRegions]);

  React.useEffect(() => {
    setIsLoading(isFetching || isFetchingPartners || isUserRegionsFetching);
  }, [isFetching, isFetchingPartners, isUserRegionsFetching]);

  const handleEdit = (licensedTitle: ILicensedTitle) => {
    history.push(runLimitRoutes.paths.licensedTitlesEditPage.replace(':id', licensedTitle.id));
  };

  const search = (clear = false) => {
    setSkipSearch(true);

    if (clear) {
      setSearchModel({});
      setSearchObj({});
    } else {
      setSearchObj({...searchModel});
    }

    setSortCol('Title');
    setSortDir(1);
    setPage(0);
  };

  const changeSort = (columnName: keyof typeof TABLE_COLUMN_NAME) => {
    if (columnName === sortCol) {
      if (sortDir === 1) {
        setSortDir(2);
      } else {
        setSortDir(1);
      }
    } else {
      setSortCol(columnName);
      setSortDir(1);
    }

    setPage(0);
  };

  if (!ableTo('LICENSED_TITLES_VIEW')) {
    return <NotAuthorized />;
  }

  if (isError || isErrorPartners || isUserRegionsError) {
    return <CrudError error='Error loading page data' />;
  }

  return (
    <Sidebar fullHeight={true}>
      <Expand width='18.75rem' height='100%' fullHeightContainer={true} isExpanded={isSearchExpanded}>
        <Template label='expandable'>
          <Box background='pewter' padding='medium' fullHeight={true}>
            <Cover scrolling={true} gutter='medium'>
              <Template label='header'>
                <Stack space='medium'>
                  <Cluster align='center' justify='space-between'>
                    <Icon icon='tune' space='small' size='large' iconAlign='center'>
                      <Heading level='h4'>Search Filters</Heading>
                    </Icon>
                    <Icon icon='collapseleft' size='large' onClick={() => setIsSearchExpanded(!isSearchExpanded)} />
                  </Cluster>
                  <Divider color='graphite' />
                </Stack>
              </Template>
              <Template label='cover'>
                <FormSearchWrapper onSearch={search}>
                  <Stack space='small'>
                    <FormItem {...searchForm.title} onBlur={() => searchOnBlur('title')}>
                      <TextInput
                        placeholder='Title'
                        value={searchModel.title}
                        onChange={val => searchOnChange('title', val)}
                      />
                    </FormItem>
                    <FormItem {...searchForm.partner}>
                      <Select
                        placeholder='Select Partner'
                        clearable={true}
                        predicate='value'
                        searchable={true}
                        value={{label: '', value: searchModel.partner}}
                        id='partner'
                        onSearch={val =>
                          (partners || [])
                            .filter(partner => partner.name.toLowerCase().indexOf(val.toLowerCase()) > -1)
                            .map(partner => ({label: partner.name, value: partner.id})) || []
                        }
                        options={(partners || []).map(partner => ({label: partner.name, value: partner.id}))}
                        onChange={val => searchSetFields({partner: (val as ISelectOption)?.value})}
                      />
                    </FormItem>
                    <FormItem {...searchForm.regions}>
                      <Select
                        placeholder='Select Region(s)'
                        clearable={true}
                        options={activeRegions.map(ar => ({
                          label: `${ar.name} (${ar.code})`,
                          value: ar.code.toLowerCase(),
                        }))}
                        predicate='value'
                        multiselect={true}
                        value={searchModel.regions?.map(region => ({label: region, value: region}))}
                        onChange={val =>
                          searchSetFields({
                            regions: ((val as ISelectOption[]) || []).map(option => option.value),
                          })
                        }
                      />
                    </FormItem>
                    <FormItem {...searchForm.programmingType}>
                      <Select
                        clearable={true}
                        placeholder='Select a Programming Type'
                        onChange={value => searchSetFields({programmingType: (value as ISelectOption)?.label as any})}
                        value={{label: (searchModel.programmingType as string) || ''}}
                        options={programmingTypes.map(pT => ({label: pT}))}
                      />
                    </FormItem>
                  </Stack>
                </FormSearchWrapper>
              </Template>
              <Template label='footer'>
                <Cluster justify='space-between'>
                  <div></div>
                  <Cluster space='xxxsmall'>
                    <Button state={isFetching ? 'disabled' : ''} onClick={() => search(true)}>
                      Clear
                    </Button>
                    <Button type='primary' state={isFetching ? 'thinking' : ''} onClick={() => search()}>
                      Search
                    </Button>
                  </Cluster>
                </Cluster>
              </Template>
            </Cover>
          </Box>
        </Template>
      </Expand>
      <Cover
        scrolling={true}
        gutter='large'
        coverTemplateHeight='100%'
        overflow='auto'
        padding={{mobile: 'medium', wide: 'large'}}
      >
        <Template label='header'>
          <Cluster justify='space-between' align='center' space='medium'>
            <Cluster align='end' space='small'>
              <Heading level='h1'>Licensed Titles</Heading>
              <Cluster space='xxsmall' align='center'>
                <Icon
                  icon='tune'
                  space='xxxsmall'
                  verticalAlign='bottom'
                  lineHeight='0px'
                  onClick={() => setIsSearchExpanded(!isSearchExpanded)}
                >
                  {licensedTitles?.metadata.totalCount || 0} Items
                </Icon>
              </Cluster>
            </Cluster>
          </Cluster>
        </Template>
        <Template label='cover'>
          <Box
            background='pewter'
            borderTop={true}
            borderSize='2px'
            borderColor='cavern'
            paddingTop={((licensedTitles?.data || []).length as number) > 0 ? 'xsmall' : 'medium'}
            paddingBottom='none'
            paddingX='large'
            fullHeight={true}
          >
            <Table
              loading={isLoading}
              fixedHeader={true}
              wrapContent={true}
              id='lt-table'
              onSort={column => changeSort(column as keyof typeof TABLE_COLUMN_NAME)}
              sortDir={sortDir === 1 ? 'asc' : 'dsc'}
              sortCol={sortCol}
              cols={[
                {
                  label: 'Title',
                  sortable: true,
                  colMinWidth: '450px',
                  transform: row => (
                    <TdLink
                      row={row}
                      title={row.title || row.titleClipData?.[0].title}
                      onClick={row => handleEdit(row)}
                    />
                  ),
                },
                {
                  label: 'Partner',
                  sortable: true,
                  colMinWidth: '200px',
                  transform: row => row.partnerName || row.titleClipData?.[0].partner?.name,
                },
                {
                  label: 'Pluto Avails ID',
                  field: 'plutoAvailsId',
                  colMinWidth: '200px',
                },
                {
                  label: 'Active Region(s)',
                  transform: row => row.activeRegion.toUpperCase(),
                  sortable: true,
                  colMinWidth: '170px',
                },
                {
                  label: 'Avail. Window',
                  colMinWidth: '230px',
                  transform: row => (
                    <RunLimits key={row.id} isWindow={true} licensedTitleId={row.id} getPromise={getPromise} />
                  ),
                },
                {
                  label: 'Programming Type',
                  colMinWidth: '230px',
                  transform: row => (row.programmingType ? row.programmingType : 'N/A'),
                },
                {
                  label: 'Run Limits',
                  colMinWidth: '150px',
                  transform: row => <RunLimits key={row.id} licensedTitleId={row.id} getPromise={getPromise} />,
                },
                ...(ableTo('LICENSED_TITLES_EDIT')
                  ? [
                      {
                        label: 'Actions',
                        colWidth: '100px',
                        transform: row => (
                          <TableActions
                            row={row}
                            icons={['edit']}
                            onClick={(row, icon) => {
                              switch (icon) {
                                case 'edit':
                                  handleEdit(row);
                                  break;
                                default:
                              }
                            }}
                          />
                        ),
                      } as ITableCol<ILicensedTitle>,
                    ]
                  : []),
              ]}
              rows={licensedTitles?.data || []}
            >
              <Template label='loading'>
                <Cluster space='small' align='center'>
                  <Spinner />
                  <Paragraph>Loading Clips</Paragraph>
                </Cluster>
              </Template>
              <Template label='empty'>
                <Notification type='warning'>
                  <Paragraph>There are no clips currently available.</Paragraph>
                </Notification>
              </Template>
            </Table>
          </Box>
        </Template>
        <Template label='footer'>
          <Cluster justify='space-between'>
            <div></div>
            {((licensedTitles?.data || []).length as number) > 0 && (
              <Pagination
                perPage={rowsPerPage as 25 | 50 | 75 | 100}
                currentPage={page}
                total={licensedTitles?.metadata.totalCount || 0}
                onPageChange={page => {
                  setPage(page);
                  document.getElementById('lt-table')?.parentElement?.scrollTo(0, 0);
                }}
                onPerPageChange={perPage => {
                  setRowsPerPage(perPage);
                  setPage(0);
                }}
              />
            )}
          </Cluster>
        </Template>
      </Cover>
    </Sidebar>
  );
};
